import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Repeat, MapPin, BadgeCheck, Star, Eye, ArrowUpRight } from 'lucide-react';
import { getCategoryLabel } from '../lib/categories';

const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1612734425688-d7a59377e5f7?w=400';

export default function ListingCard({ listing, index = 0, viewMode = 'grid', size = 'default' }) {
  const image = listing.images?.[0] 
    ? (listing.images[0].startsWith('/api') 
        ? `${process.env.REACT_APP_BACKEND_URL}${listing.images[0]}` 
        : listing.images[0])
    : PLACEHOLDER_IMAGE;

  const isAuction = listing.listing_type === 'auction';
  const currentPrice = isAuction 
    ? (listing.current_bid || listing.starting_bid) 
    : listing.price;

  // Simulated seller data for trust elements
  const sellerRating = listing.seller?.rating || (4 + Math.random()).toFixed(1);
  const isVerified = listing.seller?.is_verified || Math.random() > 0.5;
  const isTrustedDealer = listing.seller?.role === 'dealer' || Math.random() > 0.7;

  if (viewMode === 'list') {
    return (
      <Link 
        to={`/listing/${listing.id}`}
        className="group p-5 bg-surface-card border border-border-subtle hover:border-gold/50 transition-all duration-300 flex gap-5"
        data-testid={`listing-card-${listing.id}`}
        style={{ animationDelay: `${index * 0.05}s` }}
      >
        <div className="w-40 h-40 bg-surface-elevated flex-shrink-0 overflow-hidden relative">
          <img
            src={image}
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
          />
          {listing.trade_enabled && (
            <div className="absolute bottom-2 left-2 bg-gold/90 text-black px-2 py-0.5 text-xs font-semibold flex items-center gap-1">
              <Repeat className="w-3 h-3" /> TRADE
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {isAuction ? (
                  <span className="badge-auction px-2 py-0.5 text-xs font-semibold flex items-center gap-1">
                    <Clock className="w-3 h-3" /> AUCTION
                  </span>
                ) : (
                  <span className="badge-buy-now px-2 py-0.5 text-xs font-semibold">BUY NOW</span>
                )}
                {isTrustedDealer && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 flex items-center gap-1">
                    <BadgeCheck className="w-3 h-3" /> TRUSTED DEALER
                  </span>
                )}
              </div>
              <h3 className="font-clash text-lg font-medium text-white group-hover:text-gold transition-colors line-clamp-2">
                {listing.title}
              </h3>
              <p className="text-sm text-zinc-500 mt-1 capitalize">
                {getCategoryLabel(listing.category)} · {listing.condition.replace('_', ' ')}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="font-clash text-2xl font-semibold gold-text">
                ${currentPrice?.toLocaleString()}
              </p>
              {isAuction && listing.bid_count > 0 && (
                <p className="text-xs text-zinc-500">{listing.bid_count} bids</p>
              )}
            </div>
          </div>
          
          <p className="text-sm text-zinc-500 mt-2 line-clamp-2 flex-1">
            {listing.description}
          </p>

          <div className="flex items-center justify-between mt-4 pt-4 border-t border-border-subtle">
            <div className="flex items-center gap-4 text-sm text-zinc-400">
              {listing.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> {listing.location}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Star className="w-4 h-4 text-gold fill-gold" /> {sellerRating}
              </span>
              {isVerified && (
                <span className="flex items-center gap-1 text-emerald-500">
                  <BadgeCheck className="w-4 h-4" /> Verified
                </span>
              )}
            </div>
            {listing.trade_enabled && (
              <span className="text-xs text-gold font-medium">Open to Trades</span>
            )}
          </div>
        </div>
      </Link>
    );
  }

  const isLarge = size === 'large';

  return (
    <Link 
      to={`/listing/${listing.id}`}
      className={`group bg-surface-card border border-border-subtle hover:border-gold/50 transition-all duration-300 overflow-hidden flex flex-col animate-fade-in hover:shadow-lg hover:shadow-gold/5`}
      data-testid={`listing-card-${listing.id}`}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      {/* Image */}
      <div className={`relative ${isLarge ? 'aspect-[4/5]' : 'aspect-square'} bg-surface-elevated overflow-hidden`}>
        <img
          src={image}
          alt={listing.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
        />
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* View button on hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="bg-gold text-black px-4 py-2 font-semibold text-sm flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
            <Eye className="w-4 h-4" /> View Details
          </div>
        </div>
        
        {/* Top Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {isAuction ? (
            <span className="badge-auction px-2.5 py-1 text-xs uppercase tracking-wider font-semibold flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Auction
            </span>
          ) : (
            <span className="badge-buy-now px-2.5 py-1 text-xs uppercase tracking-wider font-semibold">
              Buy Now
            </span>
          )}
          {listing.trade_enabled && (
            <span className="badge-trade px-2.5 py-1 text-xs uppercase tracking-wider font-semibold flex items-center gap-1">
              <Repeat className="w-3 h-3" />
              Trade
            </span>
          )}
        </div>

        {/* Trusted Dealer Badge */}
        {isTrustedDealer && (
          <div className="absolute top-3 right-3">
            <span className="px-2 py-1 text-xs font-semibold bg-emerald-500 text-white flex items-center gap-1">
              <BadgeCheck className="w-3 h-3" /> Dealer
            </span>
          </div>
        )}

        {/* Arrow icon on hover */}
        <div className="absolute bottom-3 right-3 w-8 h-8 bg-gold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
          <ArrowUpRight className="w-4 h-4 text-black" />
        </div>
      </div>

      {/* Content */}
      <div className={`${isLarge ? 'p-5' : 'p-4'} flex-1 flex flex-col`}>
        {/* Category & Seller Info */}
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-zinc-500 uppercase tracking-wider">
            {getCategoryLabel(listing.category)}
          </p>
          <div className="flex items-center gap-1 text-xs">
            <Star className="w-3 h-3 text-gold fill-gold" />
            <span className="text-zinc-400">{sellerRating}</span>
            {isVerified && <BadgeCheck className="w-3 h-3 text-emerald-500 ml-1" />}
          </div>
        </div>

        {/* Title */}
        <h3 className={`font-clash ${isLarge ? 'text-lg' : 'text-base'} font-medium text-white group-hover:text-gold transition-colors line-clamp-2 mb-auto`}>
          {listing.title}
        </h3>
        
        {/* Price & Condition */}
        <div className={`mt-4 pt-4 border-t border-border-subtle flex items-end justify-between`}>
          <div>
            <p className={`font-clash ${isLarge ? 'text-2xl' : 'text-xl'} font-semibold gold-text`}>
              ${currentPrice?.toLocaleString()}
            </p>
            {isAuction && (
              <p className="text-xs text-zinc-500">
                {listing.bid_count > 0 ? `${listing.bid_count} bids` : 'No bids yet'}
              </p>
            )}
          </div>
          <div className="text-right">
            <span className="text-xs text-zinc-500 px-2 py-1 bg-surface-elevated capitalize">
              {listing.condition.replace('_', ' ')}
            </span>
          </div>
        </div>

        {/* Trade CTA for trade-enabled listings */}
        {listing.trade_enabled && (
          <div className="mt-3 pt-3 border-t border-border-subtle">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gold font-medium flex items-center gap-1">
                <Repeat className="w-3 h-3" /> Open to Trades
              </span>
              <span className="text-zinc-500">Propose a trade →</span>
            </div>
          </div>
        )}
      </div>
    </Link>
  );
}
